import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface LoginHistory {
  id: string;
  user_id: string;
  ip_address: string | null;
  user_agent: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  location: string | null;
  status: string | null;
  created_at: string | null;
}

function parseUserAgent(ua: string): { browser: string; os: string; device_type: string } {
  const browser = ua.includes("Chrome") ? "Chrome" :
    ua.includes("Firefox") ? "Firefox" :
    ua.includes("Safari") ? "Safari" :
    ua.includes("Edge") ? "Edge" :
    ua.includes("Opera") ? "Opera" : "Outro";

  const os = ua.includes("Windows") ? "Windows" :
    ua.includes("Mac") ? "macOS" :
    ua.includes("Linux") ? "Linux" :
    ua.includes("Android") ? "Android" :
    ua.includes("iPhone") || ua.includes("iPad") ? "iOS" : "Outro";

  const device_type = ua.includes("Mobile") || ua.includes("Android") || ua.includes("iPhone") 
    ? "Mobile" : "Desktop";

  return { browser, os, device_type };
}

export function useLoginHistory() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["login-history", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("login_history")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as LoginHistory[];
    },
    enabled: !!user?.id,
  });
}

export function useLogLogin() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("User not authenticated");

      const userAgent = navigator.userAgent;
      const { browser, os, device_type } = parseUserAgent(userAgent);

      const { error } = await supabase.from("login_history").insert({
        user_id: user.id,
        user_agent: userAgent,
        browser,
        os,
        device_type,
        status: "success",
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["login-history"] });
    },
  });
}

export function useRevokeAllSessions() {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("User not authenticated");
      
      // Sign out from all sessions by signing out globally
      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["login-history"] });
    },
  });
}
